// src/pages/Users/Inscription/EditDocumentsModal.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "../../../components/Button/Button";
import {
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
  deleteDossierFile,
} from "../../../services/dossierServices";
import {
  FaTimes,
  FaSave,
  FaTrashAlt,
  FaPlusCircle,
  FaGoogleDrive,
  FaFilePdf,
  FaImage,
  FaLink,
  FaTimesCircle,
  FaFileMedical,
  FaFileSignature,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

/* ---------------- Helpers ---------------- */
const SERVER_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/+$/, "");

const toAbsoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const clean = u.startsWith("/") ? u : `/${u}`;
  return `${SERVER_BASE}${clean}`;
};

const isDrive = (u) =>
  !!u &&
  /https?:\/\/(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
    u
  );
const isImage = (u) =>
  !!u && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u.split("?")[0]);
const isPdf = (u) =>
  !!u && (u.toLowerCase().endsWith(".pdf") || u.toLowerCase().includes("pdf"));

/* Icône “état document” (gros bouton cliquable) */
const BigDocButton = ({ url, onClick }) => {
  const has = !!url;
  let Icon = FaTimesCircle;
  let color = "text-slate-300";
  let label = "Aucun document";

  if (has) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;
      color = "text-sky-600";
      label = "Lien Google Drive";
    } else if (isPdf(url)) {
      Icon = FaFilePdf;
      color = "text-violet-600";
      label = "Fichier PDF";
    } else if (isImage(url)) {
      Icon = FaImage;
      color = "text-emerald-600";
      label = "Image";
    } else {
      Icon = FaLink;
      color = "text-slate-600";
      label = "Lien";
    }
  }

  return (
    <button
      type="button"
      onClick={has ? onClick : undefined}
      className={`group w-full flex flex-col items-center justify-center rounded-2xl border border-dashed p-4 sm:p-5 transition
        ${
          has
            ? "border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            : "border-slate-300 bg-slate-50/50 dark:bg-slate-900/30 cursor-not-allowed"
        }`}
      title={has ? "Ouvrir le document" : "Aucun document"}
    >
      <Icon
        className={`text-4xl sm:text-5xl mb-1.5 sm:mb-2 ${color} ${
          has ? "group-hover:scale-105" : ""
        }`}
      />
      <div
        className={`text-xs sm:text-sm ${
          has ? "text-slate-800 dark:text-slate-100" : "text-slate-400"
        }`}
      >
        {label}
      </div>
      {has && (
        <div className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs text-slate-500">
          Cliquer pour ouvrir
        </div>
      )}
    </button>
  );
};

/* Cadre section générique (sans badge de validation) */
const SectionCard = ({ title, icon, children }) => (
  <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/70 p-4">
    <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
      <span className="text-base sm:text-lg">{icon}</span>
      <h4 className="font-semibold text-sm sm:text-base">{title}</h4>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

/* ---------------- Main Modal ---------------- */
const EditDocumentsModal = ({ isOpen, onClose, dossier, refreshDossiers }) => {
  // Hooks en haut, sans condition
  const [saving, setSaving] = useState(false);

  // États pour afficher/masquer les formulaires (fermés par défaut)
  const [showFormCM, setShowFormCM] = useState(false);
  const [showFormSig, setShowFormSig] = useState(false);
  const [showFormLic, setShowFormLic] = useState(false);

  // Modes & inputs pour chaque section
  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState(""); // indépendant

  const [modeSig, setModeSig] = useState("upload");
  const [fileSig, setFileSig] = useState(null);
  const [urlSig, setUrlSig] = useState("");

  const [modeLic, setModeLic] = useState("upload");
  const [fileLic, setFileLic] = useState(null);
  const [urlLic, setUrlLic] = useState("");

  const dateInputRef = useRef(null);

  // Valeurs actuelles (compat noms de colonnes)
  const currentCM = dossier?.cm_url || "";
  const currentSignee =
    dossier?.formulaire_licence_signe ||
    dossier?.formulaire_licence_signee ||
    "";
  const currentLicence =
    dossier?.licence_url ||
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    "";

  // (Ré)initialisation à l’ouverture
  useEffect(() => {
    if (!isOpen || !dossier?.id) return;

    setShowFormCM(false);
    setShowFormSig(false);
    setShowFormLic(false);

    // Init modes/inputs avec les valeurs actuelles
    setModeCM(
      currentCM && currentCM.startsWith("/uploads/")
        ? "upload"
        : currentCM
        ? "link"
        : "upload"
    );
    setFileCM(null);
    setUrlCM(currentCM || "");
    setDateCM(toInputDateSafe(dossier?.date_cm)); // valeur indépendante

    setModeSig(
      currentSignee && currentSignee.startsWith("/uploads/")
        ? "upload"
        : currentSignee
        ? "link"
        : "upload"
    );
    setFileSig(null);
    setUrlSig(currentSignee || "");

    setModeLic(
      currentLicence && currentLicence.startsWith("/uploads/")
        ? "upload"
        : currentLicence
        ? "link"
        : "upload"
    );
    setFileLic(null);
    setUrlLic(currentLicence || "");

    setSaving(false);
  }, [isOpen, dossier?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !dossier?.id) return null;

  /* ---------- Actions génériques ---------- */
  const openUrl = (u) => {
    if (!u) return;
    window.open(toAbsoluteUrl(u), "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;
    try {
      setSaving(true);
      await deleteDossierFile(dossier.id, field);
      toast.success(`${label} supprimé.`);
      await refreshDossiers?.();
    } catch (e) {
      console.error(e);
      toast.error(`Échec de la suppression de « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Sauvegardes par section ---------- */

  // 1) Médical — Document (fichier OU lien) — la date n’est plus obligatoire ici
  const saveCM = async () => {
    if (modeCM === "upload" && !fileCM) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeCM === "link" && urlCM && !/^https?:\/\//i.test(urlCM)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeCM === "upload") {
        // envoi du fichier; si tu veux pousser la date avec, ajoute { date_cm: dateCM || undefined }
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM ? { date_cm: dateCM } : undefined
        );
      } else {
        if (currentCM?.startsWith?.("/uploads/") && urlCM) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch {}
        }
        const payload = { cm_url: urlCM || null };
        if (dateCM) payload.date_cm = dateCM; // inclure la date seulement si saisie
        await updateDossierInscription(dossier.id, payload);
      }
      await refreshDossiers?.();
      setShowFormCM(false);
      toast.success("Document médical mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  // 1-bis) Médical — Sauvegarde indépendante de la date
  const saveCMDate = async () => {
    if (!dateCM) {
      toast.error("Indiquez la date du certificat/attestation.");
      if (dateInputRef.current) dateInputRef.current.focus();
      return;
    }
    setSaving(true);
    try {
      await updateDossierInscription(dossier.id, { date_cm: dateCM });
      await refreshDossiers?.();
      toast.success("Date du document médical enregistrée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  // 2) Formulaire signé
  const saveSignee = async () => {
    if (modeSig === "upload" && !fileSig) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeSig === "link" && urlSig && !/^https?:\/\//i.test(urlSig)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeSig === "upload") {
        await uploadFormulaireLicenceSigne(dossier.id, fileSig);
      } else {
        if (currentSignee?.startsWith?.("/uploads/") && urlSig) {
          try {
            await deleteDossierFile(dossier.id, "formulaire_licence_signe");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: urlSig || null,
          formulaire_licence_signee: urlSig || null,
        });
      }
      await refreshDossiers?.();
      setShowFormSig(false);
      toast.success("Formulaire signé mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // 3) Licence (optionnelle)
  const saveLicence = async () => {
    if (modeLic === "upload" && !fileLic) {
      toast.error("Choisissez un fichier (PDF ou image).");
      return;
    }
    if (modeLic === "link" && urlLic && !/^https?:\/\//i.test(urlLic)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);
    try {
      if (modeLic === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLic);
      } else {
        if (currentLicence?.startsWith?.("/uploads/") && urlLic) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          licence_url: urlLic || null,
          licence_assurance_url: urlLic || null,
          licence_assurance: urlLic || null,
        });
      }
      await refreshDossiers?.();
      setShowFormLic(false);
      toast.success("Licence / Assurance mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // Prend 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ', Date, etc. et renvoie 'YYYY-MM-DD'
  const toInputDateSafe = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  /* ---------- UI ---------- */

  const Header = (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
      <div>
        <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
          Documents d’inscription
        </h3>
        <p className="text-white/90 text-[11px] sm:text-xs">
          Cliquez sur l’icône pour ouvrir. Utilisez “Ajouter/Modifier” pour
          déposer un fichier ou un lien. “Supprimer” retire le document.
        </p>
      </div>
      <button
        onClick={() => !saving && onClose?.()}
        className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
        aria-label="Fermer"
        type="button"
        title="Fermer"
      >
        <FaTimes />
      </button>
    </div>
  );

  const FormUploadOrLink = ({
    mode,
    setMode,
    file,
    setFile,
    url,
    setUrl,
    extraBelow,
  }) => (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-[13px] sm:text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            value="upload"
            checked={mode === "upload"}
            onChange={() => setMode("upload")}
          />
          Téléverser un fichier
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            value="link"
            checked={mode === "link"}
            onChange={() => setMode("link")}
          />
          Coller un lien (Drive)
        </label>
      </div>

      {mode === "upload" ? (
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
        />
      ) : (
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://drive.google.com/..."
          className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
        />
      )}

      {extraBelow}
    </div>
  );

  // Petites classes utilitaires pour boutons compacts
  const btnSmall = "!rounded-lg !px-2.5 !py-1.5 !text-xs";
  const btnDangerSmall = `${btnSmall} !bg-rose-600 hover:!bg-rose-700`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose?.()}
    >
      <div
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {Header}

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Médical */}
          <SectionCard
            title="Médical (certificat / attestation)"
            icon={<FaFileMedical />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentCM}
                onClick={() => openUrl(currentCM)}
              />
              {/* Boutons compacts, sur une seule ligne */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentCM ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormCM((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentCM && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() => removeField("cm_url", "Document médical")}
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {/* Saisie indépendante de la date (SOUS les boutons) */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className="block text-[13px] font-medium mb-1">
                  Date figurant sur le document
                </label>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateCM}
                  onChange={(e) => setDateCM(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[12px] text-slate-500">
                  Vous pouvez enregistrer la date indépendamment du
                  fichier/lien.
                </p>
              </div>
              <Button
                type="button"
                text={saving ? "Enregistrement…" : "Enregistrer la date"}
                icon={FaSave}
                onClick={saveCMDate}
                disabled={saving}
                className={`${btnSmall} gap-2`}
              />
            </div>

            {/* Formulaire fichier/lien (affiché à la demande) */}
            {showFormCM && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeCM}
                  setMode={setModeCM}
                  file={fileCM}
                  setFile={setFileCM}
                  url={urlCM}
                  setUrl={setUrlCM}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={
                          saving ? "Enregistrement…" : "Enregistrer le document"
                        }
                        icon={FaSave}
                        onClick={saveCM}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormCM(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Formulaire signé */}
          <SectionCard
            title="Formulaire de licence signé"
            icon={<FaFileSignature />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentSignee}
                onClick={() => openUrl(currentSignee)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentSignee ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormSig((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentSignee && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() =>
                      removeField(
                        "formulaire_licence_signe",
                        "Formulaire signé"
                      )
                    }
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {showFormSig && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeSig}
                  setMode={setModeSig}
                  file={fileSig}
                  setFile={setFileSig}
                  url={urlSig}
                  setUrl={setUrlSig}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={saving ? "Enregistrement…" : "Enregistrer"}
                        icon={FaSave}
                        onClick={saveSignee}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormSig(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Licence (optionnelle) */}
          <SectionCard
            title="Licence / Assurance (optionnel)"
            icon={<FaCheckCircle />}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <BigDocButton
                url={currentLicence}
                onClick={() => openUrl(currentLicence)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentLicence ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormLic((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />
                {currentLicence && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() =>
                      removeField("licence_url", "Licence / Assurance")
                    }
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            {showFormLic && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <FormUploadOrLink
                  mode={modeLic}
                  setMode={setModeLic}
                  file={fileLic}
                  setFile={setFileLic}
                  url={urlLic}
                  setUrl={setUrlLic}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={saving ? "Enregistrement…" : "Enregistrer"}
                        icon={FaSave}
                        onClick={saveLicence}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />
                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormLic(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditDocumentsModal;
